import { useState } from 'react';
import { useInvalidateNotifications } from '@/entities/notification';
import { trpc } from '@/shared/api';
import { getErrorMessage, getTrpcErrorCode } from '@/shared/lib';

export interface UseRemoveNotificationOperatorRecipientResult {
  /** 성공하면(설정을 다시 읽은 뒤) true. 서버가 거부하면 false 이고 문구는 error 에 남는다. */
  readonly removeRecipient: (id: string) => Promise<boolean>;
  /** 요청과 그 뒤 설정 재조회가 끝날 때까지 true — 지운 행이 사라지기 전에 다시 누르지 못하게 한다. */
  readonly isPending: boolean;
  readonly error: string | null;
}

const FORM_ERROR_FALLBACK = '담당자를 삭제하지 못했습니다.';

/**
 * 운영자 수신자 삭제(notificationManagement.removeOperatorRecipient, 알림 설계 N4). 마지막 한 명을 지우면 운영자 알림은 환경 변수
 * 기본값(QUOTE_NOTIFY_EMAIL)으로 돌아간다. 다른 운영자가 먼저 지워 없으면(NOT_FOUND) 설정을 다시 읽어 화면에 남은 행을 걷어 낸다.
 */
export const useRemoveNotificationOperatorRecipient =
  (): UseRemoveNotificationOperatorRecipientResult => {
    const [error, setError] = useState<string | null>(null);
    const invalidate = useInvalidateNotifications();
    const mutation = trpc.notificationManagement.removeOperatorRecipient.useMutation({
      onSuccess: () => invalidate.invalidateSettings(),
    });

    const removeRecipient = async (id: string): Promise<boolean> => {
      setError(null);

      try {
        await mutation.mutateAsync({ id });
        return true;
      } catch (cause) {
        setError(getErrorMessage(cause, FORM_ERROR_FALLBACK));

        if (getTrpcErrorCode(cause) === 'NOT_FOUND') {
          await invalidate.invalidateSettings();
        }

        return false;
      }
    };

    return { removeRecipient, isPending: mutation.isPending, error };
  };
